/**
 * Club Handle Helper Utilities
 * Extracts club handles from various sources (URL, cart items, etc.)
 */

import type { CartItemResponse } from '@/features/cart/types';
import type { CartResponse } from '@/features/cart/types';

/**
 * Extracts club handle from URL using React Router
 * This is a utility function that can be used in components with useParams
 * @param params - Route params object (from useParams hook)
 * @returns Club handle or null
 */
export const getClubHandleFromParams = (params: { handle?: string }): string | null => {
  return params.handle || null;
};

/**
 * Extracts club handle from a cart item
 * @param cartItem - The cart item response
 * @returns Club handle or null
 */
export const getClubHandleFromCartItem = (cartItem: CartItemResponse): string | null => {
  if (!cartItem) {
    return null;
  }

  // Try to get handle from event_booking_category -> booking_category -> business -> handle
  const handle =
    cartItem.event_booking_category?.booking_category?.business?.handle ||
    cartItem.event_booking_category?.event_schedule?.event?.business?.handle;

  // If still null, try to get from add-ons (all add-ons should have the same business)
  if (!handle && cartItem.add_ons_per_guest) {
    for (const guestAddOns of cartItem.add_ons_per_guest) {
      if (guestAddOns && guestAddOns.length > 0) {
        // We can't get handle from add-ons directly in CartItemResponse
        // This would require access to the full addOns array
        break;
      }
    }
  }

  return handle || null;
};

/**
 * Extracts club handle from a cart
 * @param cart - The cart response
 * @returns Club handle or null
 */
export const getClubHandleFromCart = (cart: CartResponse | null): string | null => {
  if (!cart) {
    return null;
  }

  return cart.business?.handle || null;
};

/**
 * Extracts club handle from URL pathname
 * Useful when you don't have access to useParams
 * @param pathname - The current pathname (from useLocation().pathname)
 * @returns Club handle or null
 */
export const getClubHandleFromPathname = (pathname: string): string | null => {
  if (!pathname) {
    return null;
  }

  // Match patterns like /venues/:handle or /venues/:handle/...
  const match = pathname.match(/^\/venues\/([^/]+)/);
  return match ? match[1] : null;
};

/**
 * Gets club handle from multiple sources, in order of priority
 * @param sources - Object containing various sources to check
 * @returns Club handle or null
 */
export const getClubHandle = (sources: {
  params?: { handle?: string };
  pathname?: string;
  cartItem?: CartItemResponse;
  cart?: CartResponse | null;
}): string | null => {
  // Priority order:
  // 1. URL params (most reliable)
  // 2. Cart item
  // 3. Cart
  // 4. Pathname (fallback)

  if (sources.params?.handle) {
    return sources.params.handle;
  }

  if (sources.cartItem) {
    const handle = getClubHandleFromCartItem(sources.cartItem);
    if (handle) {
      return handle;
    }
  }

  if (sources.cart) {
    const handle = getClubHandleFromCart(sources.cart);
    if (handle) {
      return handle;
    }
  }

  if (sources.pathname) {
    return getClubHandleFromPathname(sources.pathname);
  }

  return null;
};

